import { decodeHtmlWithWhiteList } from 'core/utils/strings';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { Button, Dialog, Icon, SelectCustomizable } from 'ui';

const findCourseById = (courses, courseId) => {
  if (!courseId || !courses?.length) return null;
  return courses.find((c) => c.id === courseId || c.guid === courseId);
};

const LessonSelectionDialog = ({
  t,
  open,
  onClose,
  onSelect,
  courses,
  units,
  loadingUnits,
  loadCourseUnits,
  loadingCourses,
  contentError,
  alreadySelectedLessons = [],
  multiSelect = true,
  defaultCourseId,
}) => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedLessonGuids, setSelectedLessonGuids] = useState(new Set());
  const [expandedUnits, setExpandedUnits] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Single-select state (used when multiSelect=false)
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  // Initialize from already-selected lessons when dialog opens
  useEffect(() => {
    if (open) {
      if (multiSelect) {
        if (alreadySelectedLessons.length > 0) {
          const guids = new Set(alreadySelectedLessons.map((l) => l.guid || l.lessonGuid));
          setSelectedLessonGuids(guids);

          // Auto-select the course from the first already-selected lesson
          const firstLesson = alreadySelectedLessons[0];
          if (firstLesson?.courseGuid && courses?.length > 0) {
            const course = courses.find((c) => c.id === firstLesson.courseGuid);
            if (course) {
              const option = { value: course.id, label: course.title, data: course };
              setSelectedCourse(option);
              loadCourseUnits(course.id);
            }
          }
        } else {
          setSelectedLessonGuids(new Set());
          setExpandedUnits(new Set());
          setSearchQuery('');
          // Preselect default course if available
          const defaultOption =
            defaultCourseId && courses?.length > 0
              ? (() => {
                const c = findCourseById(courses, defaultCourseId);
                return c ? { value: c.id, label: c.title, data: c } : null;
              })()
              : null;
          if (defaultOption) {
            setSelectedCourse(defaultOption);
            loadCourseUnits(defaultOption.value);
          } else {
            setSelectedCourse(null);
          }
        }
      } else {
        // Single-select: reset state
        setSelectedUnit(null);
        setSelectedLesson(null);
        const defaultOption =
          defaultCourseId && courses?.length > 0
            ? (() => {
              const c = findCourseById(courses, defaultCourseId);
              return c ? { value: c.id, label: c.title, data: c } : null;
            })()
            : null;
        if (defaultOption) {
          setSelectedCourse(defaultOption);
          loadCourseUnits(defaultOption.value);
        } else {
          setSelectedCourse(null);
        }
      }
    }
  }, [open]);

  // Handle async race condition: courses may load after dialog opens
  useEffect(() => {
    if (open && !selectedCourse && defaultCourseId && courses?.length > 0) {
      const course = findCourseById(courses, defaultCourseId);
      if (course) {
        const option = { value: course.id, label: course.title, data: course };
        setSelectedCourse(option);
        loadCourseUnits(option.value);
      }
    }
  }, [open, courses, defaultCourseId]);

  const courseOptions = useMemo(() => {
    if (!courses || courses.length === 0) return [];
    return courses.map((item) => ({
      value: item.id,
      label: item.title,
      data: item,
    }));
  }, [courses]);

  // Filter units and lessons by search query
  const filteredUnits = useMemo(() => {
    if (!selectedCourse || !units || units.length === 0) return [];

    const query = searchQuery.toLowerCase().trim();
    if (!query) return units;

    return units
      .map((unit) => {
        const unitTitle = decodeHtmlWithWhiteList(unit.title || '').toLowerCase();
        const unitMatches = unitTitle.includes(query);

        const filteredLessons = (unit.lessons || []).filter((lesson) => {
          const lessonTitle = decodeHtmlWithWhiteList(lesson.title || '').toLowerCase();
          return lessonTitle.includes(query);
        });

        // Include unit if unit name matches OR any lesson matches
        if (unitMatches || filteredLessons.length > 0) {
          return {
            ...unit,
            lessons: unitMatches ? unit.lessons || [] : filteredLessons,
            _autoExpand: filteredLessons.length > 0 && !unitMatches,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [selectedCourse, units, searchQuery]);

  // Auto-expand units that have search matches
  useEffect(() => {
    if (searchQuery.trim()) {
      const toExpand = new Set(expandedUnits);
      filteredUnits.forEach((unit) => {
        if (unit._autoExpand) {
          toExpand.add(unit.id);
        }
      });
      setExpandedUnits(toExpand);
    }
  }, [filteredUnits, searchQuery]);

  const totalSelectedCount = selectedLessonGuids.size;

  const isModulesOnlyUnit = useCallback((unit) => {
    return !(unit.lessons?.length > 0) && unit.contents?.length > 0;
  }, []);

  const handleCourseChange = useCallback(
    (option) => {
      setSelectedCourse(option);
      setExpandedUnits(new Set());
      setSearchQuery('');

      if (!multiSelect) {
        setSelectedUnit(null);
        setSelectedLesson(null);
      }

      if (multiSelect) {
        setSelectedLessonGuids(new Set());
      }

      if (option && option.value) {
        loadCourseUnits(option.value);
      }
    },
    [loadCourseUnits, multiSelect]
  );

  // Single-select: unit options
  const unitOptions = useMemo(() => {
    if (!units || units.length === 0) return [];
    return units.map((unit, index) => ({
      value: unit.id,
      label: decodeHtmlWithWhiteList(unit.title || `${t('chatbot:Unit')} ${index + 1}`),
      data: unit,
    }));
  }, [units, t]);

  // Single-select: lesson options from selected unit
  const lessonOptions = useMemo(() => {
    if (!selectedUnit?.data?.lessons) return [];
    return selectedUnit.data.lessons.map((lesson, index) => ({
      value: lesson.id,
      label: decodeHtmlWithWhiteList(lesson.title || `${t('chatbot:Lesson')} ${index + 1}`),
      data: lesson,
    }));
  }, [selectedUnit, t]);

  const hasLessons = useMemo(() => {
    return selectedUnit?.data?.lessons?.length > 0;
  }, [selectedUnit]);

  const handleUnitChange = useCallback((option) => {
    setSelectedUnit(option);
    setSelectedLesson(null);
  }, []);

  const handleLessonChange = useCallback((option) => {
    setSelectedLesson(option);
  }, []);

  const handleSingleConfirm = useCallback(() => {
    if (!hasLessons && selectedCourse && selectedUnit) {
      // For modules-only programs, the unit acts as the lesson
      const unitData = selectedUnit.data;
      const wordCount = unitData.wordCount || (unitData.content ? unitData.content.split(/\s+/).length : 0) || null;

      const selectionData = {
        guid: selectedUnit.value,
        title: selectedUnit.label,
        wordCount: wordCount,
        courseGuid: selectedCourse.value,
        courseName: selectedCourse.label,
        unitName: selectedUnit.label,
        lessonGuid: selectedUnit.value,
        contentIds: unitData.contents?.map((c) => c.id) || [],
        type: 'module',
      };

      onSelect(selectionData);
      return;
    }

    if (!selectedLesson || !selectedCourse) return;

    const lesson = {
      guid: selectedLesson.value,
      title: selectedLesson.label,
      wordCount: null,
      courseGuid: selectedCourse.value,
      courseName: selectedCourse.label,
      unitName: selectedUnit?.label || '',
      lessonGuid: selectedLesson.value,
      contentIds: selectedLesson.data?.contents?.map((c) => c.id) || [],
      type: 'lesson',
    };

    onSelect(lesson);
  }, [selectedLesson, selectedCourse, selectedUnit, hasLessons, onSelect]);

  const toggleUnit = useCallback((unitId) => {
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  }, []);

  const toggleLesson = useCallback((lessonId) => {
    setSelectedLessonGuids((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) {
        next.delete(lessonId);
      } else {
        next.add(lessonId);
      }
      return next;
    });
  }, []);

  const toggleUnitLessons = useCallback(
    (unit) => {
      if (isModulesOnlyUnit(unit)) {
        // Modules-only: toggle the unit itself as a selectable item
        setSelectedLessonGuids((prev) => {
          const next = new Set(prev);
          if (next.has(unit.id)) {
            next.delete(unit.id);
          } else {
            next.add(unit.id);
          }
          return next;
        });
        return;
      }

      const unitLessons = unit.lessons || [];
      const unitLessonIds = unitLessons.map((l) => l.id);
      const allSelected = unitLessonIds.every((id) => selectedLessonGuids.has(id));

      setSelectedLessonGuids((prev) => {
        const next = new Set(prev);
        if (allSelected) {
          unitLessonIds.forEach((id) => next.delete(id));
        } else {
          unitLessonIds.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [selectedLessonGuids, isModulesOnlyUnit]
  );

  const handleClearAll = useCallback(() => {
    setSelectedLessonGuids(new Set());
  }, []);

  const isConfirmDisabled = hasLessons ? !selectedLesson || !selectedCourse || !selectedUnit : !selectedCourse || !selectedUnit;

  const handleConfirm = useCallback(() => {
    // Build lesson objects from selectedLessonGuids
    const lessons = [];
    (units || []).forEach((unit) => {
      if (isModulesOnlyUnit(unit)) {
        // Modules-only: the unit itself is the selectable item
        if (selectedLessonGuids.has(unit.id)) {
          lessons.push({
            guid: unit.id,
            title: decodeHtmlWithWhiteList(unit.title || ''),
            wordCount: null,
            courseGuid: selectedCourse?.value,
            courseName: selectedCourse?.label,
            unitName: decodeHtmlWithWhiteList(unit.title || ''),
            lessonGuid: unit.id,
            contentIds: unit.contents?.map((c) => c.id) || [],
            type: 'module',
          });
        }
      } else {
        (unit.lessons || []).forEach((lesson) => {
          if (selectedLessonGuids.has(lesson.id)) {
            lessons.push({
              guid: lesson.id,
              title: decodeHtmlWithWhiteList(lesson.title || ''),
              wordCount: null,
              courseGuid: selectedCourse?.value,
              courseName: selectedCourse?.label,
              unitName: decodeHtmlWithWhiteList(unit.title || ''),
              lessonGuid: lesson.id,
              contentIds: lesson.contents?.map((c) => c.id) || [],
              type: 'lesson',
            });
          }
        });
      }
    });

    onSelect(lessons);
  }, [selectedLessonGuids, units, selectedCourse, onSelect, isModulesOnlyUnit]);

  const getUnitCheckState = useCallback(
    (unit) => {
      if (isModulesOnlyUnit(unit)) {
        return selectedLessonGuids.has(unit.id) ? 'checked' : 'unchecked';
      }
      const unitLessons = unit.lessons || [];
      if (unitLessons.length === 0) return 'unchecked';
      const selectedCount = unitLessons.filter((l) => selectedLessonGuids.has(l.id)).length;
      if (selectedCount === 0) return 'unchecked';
      if (selectedCount === unitLessons.length) return 'checked';
      return 'indeterminate';
    },
    [selectedLessonGuids, isModulesOnlyUnit]
  );

  const getUnitSelectedCount = useCallback(
    (unit) => {
      if (isModulesOnlyUnit(unit)) {
        return selectedLessonGuids.has(unit.id) ? 1 : 0;
      }
      const unitLessons = unit.lessons || [];
      return unitLessons.filter((l) => selectedLessonGuids.has(l.id)).length;
    },
    [selectedLessonGuids, isModulesOnlyUnit]
  );

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  if (!multiSelect) {
    // Single-select mode: 3 cascading dropdowns
    return (
      <Dialog
        isOpen={open}
        onClose={onClose}
        onSuccess={handleSingleConfirm}
        title={t('chatbot:Choose content from program')}
        buttonTextSuccess={t('actions:Send')}
        isDisabledSuccess={isConfirmDisabled}
        size="l"
        className="lesson-selection-dialog"
      >
        <div className="lesson-selection-dialog__content">
          <div className="lesson-selection-dialog__section">
            <label className="lesson-selection-dialog__section-label">{t('chatbot:Subject')}</label>
            <SelectCustomizable
              options={courseOptions}
              value={selectedCourse}
              onChange={handleCourseChange}
              placeholder={t('chatbot:Select subject')}
              noOptionsMessage={t('chatbot:No options')}
              isSearchable={true}
              isClearable={false}
              isLoading={loadingCourses}
            />
          </div>

          <div className="lesson-selection-dialog__section">
            <label className="lesson-selection-dialog__section-label">{t('chatbot:Unit')}</label>
            <SelectCustomizable
              options={unitOptions}
              value={selectedUnit}
              onChange={handleUnitChange}
              placeholder={t('chatbot:Select unit')}
              noOptionsMessage={t('chatbot:No options')}
              isSearchable={true}
              isClearable={false}
              isLoading={loadingUnits}
              isDisabled={!selectedCourse}
            />
          </div>

          {hasLessons && (
            <div className="lesson-selection-dialog__section">
              <label className="lesson-selection-dialog__section-label">{t('chatbot:Lesson')}</label>
              <SelectCustomizable
                options={lessonOptions}
                value={selectedLesson}
                onChange={handleLessonChange}
                placeholder={t('chatbot:Select lesson')}
                noOptionsMessage={t('chatbot:No options')}
                isSearchable={true}
                isClearable={false}
                isDisabled={!selectedUnit}
              />
              {selectedLesson && (
                <div className="lesson-selection-dialog__link">
                  <a
                    href={`/course/${selectedCourse?.value}/lesson/${selectedLesson?.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lesson-selection-dialog__link-text"
                  >
                    {t('chatbot:see_book_content')}
                  </a>
                </div>
              )}
            </div>
          )}

          {contentError && (
            <div className="lesson-selection-dialog__error">
              <div className="lesson-selection-dialog__error-message">{t('chatbot:teacher_tools_error_content_unavailable_message')}</div>
            </div>
          )}
        </div>
      </Dialog>
    );
  }

  // Multi-select mode: tree with checkboxes
  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      onSuccess={handleConfirm}
      title={t('chatbot:Choose content from program')}
      buttonTextSuccess={t('chatbot:Select content')}
      isDisabledSuccess={totalSelectedCount === 0}
      size="l"
      className="lesson-selection-dialog"
      customFooterLeft={
        <span className="lesson-selection-dialog__footer-count">
          {totalSelectedCount === 1
            ? t('chatbot:lessons_chosen', { count: totalSelectedCount })
            : t('chatbot:lessons_chosen_plural', { count: totalSelectedCount })}
        </span>
      }
    >
      <div className="lesson-selection-dialog__content">
        {/* ASIGNATURA section */}
        <div className="lesson-selection-dialog__section">
          <label className="lesson-selection-dialog__section-label">{t('chatbot:Subject')}</label>
          <SelectCustomizable
            options={courseOptions}
            value={selectedCourse}
            onChange={handleCourseChange}
            placeholder={t('chatbot:Select subject')}
            noOptionsMessage={t('chatbot:No options')}
            isSearchable={true}
            isClearable={false}
            isLoading={loadingCourses}
          />
        </div>

        {/* SELECCIÓN DE TEMARIO section - always visible per Figma spec */}
        <div className="lesson-selection-dialog__section">
          <label className="lesson-selection-dialog__section-label">{t('chatbot:Syllabus selection')}</label>

          {/* Search input */}
          <div className="lesson-selection-dialog__search">
            <input
              type="text"
              className="lesson-selection-dialog__search-input"
              placeholder={t('chatbot:Search unit or lesson')}
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={!selectedCourse}
            />
            {searchQuery ? (
              <Button className="lesson-selection-dialog__search-icon" variant="plain" isIconOnly leftIcon="close" ariaLabel={t('chatbot:Close')} onClick={handleClearSearch} />
            ) : (
              <span className="lesson-selection-dialog__search-icon">
                <Icon name="search" />
              </span>
            )}
          </div>

          {/* Unit/Lesson tree */}
          <div className="lesson-selection-dialog__tree">
            {!selectedCourse ? (
              <div className="lesson-selection-dialog__empty">{t('chatbot:Select subject')}</div>
            ) : loadingUnits ? (
              <div className="lesson-selection-dialog__loading">{t('chatbot:Loading...')}</div>
            ) : filteredUnits.length === 0 ? (
              <div className="lesson-selection-dialog__empty">{searchQuery ? t('chatbot:No search results') : t('chatbot:No units available')}</div>
            ) : (
              filteredUnits.map((unit, unitIndex) => {
                const modulesOnly = isModulesOnlyUnit(unit);
                const isExpanded = expandedUnits.has(unit.id);
                const checkState = getUnitCheckState(unit);
                const selectedCount = getUnitSelectedCount(unit);
                const totalLessons = (unit.lessons || []).length;
                const unitTitle = decodeHtmlWithWhiteList(unit.title || `${t('chatbot:Unit')} ${unitIndex + 1}`);

                return (
                  <div key={unit.id} className="lesson-selection-dialog__unit">
                    <div className="lesson-selection-dialog__unit-row" onClick={() => (modulesOnly ? toggleUnitLessons(unit) : toggleUnit(unit.id))}>
                      <button
                        className={`lesson-selection-dialog__checkbox lesson-selection-dialog__checkbox--${checkState}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleUnitLessons(unit);
                        }}
                        aria-label={unitTitle}
                      >
                        {checkState === 'checked' && <Icon name="check" color="white" />}
                        {checkState === 'indeterminate' && <span className="lesson-selection-dialog__checkbox-indeterminate" />}
                      </button>
                      <div className="lesson-selection-dialog__unit-info">
                        <span className="lesson-selection-dialog__unit-name">{unitTitle}</span>
                        <span className="lesson-selection-dialog__unit-counter">
                          {modulesOnly
                            ? `${unit.contents?.length || 0} ${t('chatbot:contents')}`
                            : `${selectedCount} ${t('chatbot:of_lessons', { total: totalLessons })}`}
                        </span>
                      </div>
                      {!modulesOnly && <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} className="lesson-selection-dialog__unit-chevron" />}
                    </div>
                    {!modulesOnly && isExpanded && (
                      <div className="lesson-selection-dialog__lessons">
                        {(unit.lessons || []).map((lesson, lessonIndex) => {
                          const isSelected = selectedLessonGuids.has(lesson.id);
                          const lessonTitle = decodeHtmlWithWhiteList(lesson.title || `${t('chatbot:Lesson')} ${lessonIndex + 1}`);

                          return (
                            <div
                              key={lesson.id}
                              className={`lesson-selection-dialog__lesson-row ${isSelected ? 'lesson-selection-dialog__lesson-row--selected' : ''}`}
                              onClick={() => toggleLesson(lesson.id)}
                            >
                              <button
                                className={`lesson-selection-dialog__checkbox lesson-selection-dialog__checkbox--${isSelected ? 'checked' : 'unchecked'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleLesson(lesson.id);
                                }}
                                aria-label={lessonTitle}
                              >
                                {isSelected && <Icon name="check" color="white" />}
                              </button>
                              <span className="lesson-selection-dialog__lesson-name">{lessonTitle}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Select all / Clear actions */}
          <div className="lesson-selection-dialog__actions">
            <div>
              <Button
                text={t('chatbot:Clear selection')}
                size="xs"
                variant="primary-ghost"
                onClick={handleClearAll}
                isDisabled={!selectedCourse || totalSelectedCount === 0}
                isFullWidth
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

LessonSelectionDialog.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  courses: PropTypes.arrayOf(PropTypes.object).isRequired,
  units: PropTypes.arrayOf(PropTypes.object),
  loadingUnits: PropTypes.bool,
  loadCourseUnits: PropTypes.func.isRequired,
  loadingCourses: PropTypes.bool,
  contentError: PropTypes.string,
  alreadySelectedLessons: PropTypes.arrayOf(PropTypes.object),
  multiSelect: PropTypes.bool,
  defaultCourseId: PropTypes.string,
};

export default withTranslation(['chatbot', 'actions'])(LessonSelectionDialog);
